import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

// Accept either "HHMM" or "HH:MM" and return a normalized "HH:MM" value.
function normalizeTimeInput(value) {
  const digits = (value || "").replace(/\D/g, "").slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
}
import { Plus, Trash, Pencil, Stack, Broadcast, Eye, EyeSlash } from "@phosphor-icons/react";

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const AMBULANCE_ROLES = ["", "MVO", "Head Tech", "Crew 1", "Crew 2"];

function isoDatePart(d) { return new Date(d).toISOString().split("T")[0]; }
function nextSunday(d) {
  const c = new Date(d); c.setHours(0, 0, 0, 0);
  const offset = (7 - c.getDay()) % 7 || 7;
  c.setDate(c.getDate() + offset);
  return c;
}

export default function ScheduleTemplates() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [settings, setSettings] = useState(null);
  const [editing, setEditing] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [applyOf, setApplyOf] = useState(null);

  async function load() {
    const [t, s] = await Promise.all([
      api.get("/templates/"),
      api.get("/settings/").catch(() => ({ data: null })),
    ]);
    setTemplates(t.data); setSettings(s.data);
  }
  useEffect(() => { load(); }, []);

  function openCreate() { setEditing({ name: "", description: "", slots: [] }); setOpenDialog(true); }
  function openEdit(t) { setEditing({ ...t, slots: t.slots.map((s) => ({ ...s })) }); setOpenDialog(true); }
  async function remove(t) { if (!window.confirm(`Delete template "${t.name}"?`)) return; await api.delete(`/templates/${t.id}`); load(); }

  return (
    <AppLayout title="Schedule Templates" subtitle="Reusable weekly schedule layouts — apply to any week or month, draft or published." actions={
      <Button onClick={openCreate} className="rounded-sm bg-slate-900 text-white" data-testid="tpl-new"><Plus size={14} weight="bold" className="mr-1" />New Template</Button>
    }>
      {templates.length === 0 ? (
        <div className="surface-grid p-10 text-center">
          <Stack size={32} className="mx-auto text-slate-300 mb-2" />
          <div className="font-display text-lg font-bold mb-1">No templates yet</div>
          <div className="text-sm text-slate-500">Create one to save your standard weekly coverage and apply it weeks ahead in one click.</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {templates.map((t) => (
            <div key={t.id} className="surface-grid p-4" data-testid={`tpl-card-${t.id}`}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <div className="font-display text-lg font-bold leading-tight">{t.name}</div>
                  {t.description && <div className="text-xs text-slate-500 mt-1">{t.description}</div>}
                </div>
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="outline" className="rounded-sm h-8 text-xs" onClick={() => setApplyOf(t)} data-testid={`tpl-apply-${t.id}`}><Broadcast size={14} className="mr-1" />Apply</Button>
                  <Button size="icon" variant="ghost" onClick={() => openEdit(t)} data-testid={`tpl-edit-${t.id}`}><Pencil size={14} /></Button>
                  <Button size="icon" variant="ghost" className="text-red-600" onClick={() => remove(t)} data-testid={`tpl-del-${t.id}`}><Trash size={14} /></Button>
                </div>
              </div>
              <div className="tab-tag text-slate-500 mb-2">{t.slots.length} slot(s) per week</div>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {t.slots.map((s, i) => (
                  <div key={i} className="flex items-center justify-between text-xs border border-slate-200 bg-white rounded-sm px-2 py-1.5">
                    <div className="flex items-center gap-2">
                      <Badge className="rounded-sm bg-slate-900 text-white text-[10px]">{DOW[s.day_of_week]}</Badge>
                      <span className="mono">{s.start_time}–{s.end_time}</span>
                      <span className="font-medium">{s.position}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Badge className="rounded-sm bg-blue-600 text-white text-[10px]">{s.required_certification}</Badge>
                      <span className="tab-tag">×{s.count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <TemplateEditor open={openDialog} onOpenChange={(o) => { setOpenDialog(o); if (!o) setEditing(null); }} template={editing} setTemplate={setEditing} settings={settings} onSaved={load} />
      <ApplyTemplate apply={applyOf} onClose={() => setApplyOf(null)} onApplied={() => { setApplyOf(null); toast.success("Template applied"); }} />
    </AppLayout>
  );
}

function TemplateEditor({ open, onOpenChange, template, setTemplate, settings, onSaved }) {
  if (!template) return null;
  const positions = settings?.positions || [];
  const certs = settings?.certifications_catalog || ["EMT", "Paramedic"];

  function patch(p) { setTemplate({ ...template, ...p }); }
  function patchSlot(i, p) { const slots = [...template.slots]; slots[i] = { ...slots[i], ...p }; setTemplate({ ...template, slots }); }
  function addSlot() {
    setTemplate({
      ...template,
      slots: [...template.slots, { day_of_week: 1, start_time: "08:00", end_time: "16:00", position: positions[0] || "", required_certification: certs[0], ambulance_role: "", count: 1 }],
    });
  }
  function removeSlot(i) { setTemplate({ ...template, slots: template.slots.filter((_, idx) => idx !== i) }); }

  async function save(e) {
    e.preventDefault();
    if (!template.name.trim()) { toast.error("Name required"); return; }
    if (template.slots.length === 0) { toast.error("Add at least one slot"); return; }
    for (const s of template.slots) {
      if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(s.start_time) || !/^([01]\d|2[0-3]):[0-5]\d$/.test(s.end_time)) { toast.error("Times must be HH:MM (24h)"); return; }
      if (!s.position) { toast.error("Each slot needs a position"); return; }
    }
    try {
      const payload = { name: template.name.trim(), description: template.description || "", slots: template.slots };
      if (template.id) await api.patch(`/templates/${template.id}`, payload);
      else await api.post("/templates/", payload);
      toast.success("Template saved");
      onOpenChange(false); onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{template.id ? "Edit Template" : "New Template"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="tab-tag">Name</Label><Input required className="rounded-sm" value={template.name} onChange={(e) => patch({ name: e.target.value })} data-testid="tpl-name" /></div>
            <div><Label className="tab-tag">Description</Label><Input className="rounded-sm" value={template.description || ""} onChange={(e) => patch({ description: e.target.value })} data-testid="tpl-desc" /></div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="tab-tag">Weekly Slots</Label>
              <Button type="button" onClick={addSlot} size="sm" variant="outline" className="rounded-sm" data-testid="tpl-add-slot"><Plus size={14} className="mr-1" />Add slot</Button>
            </div>
            <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
              {template.slots.map((s, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end border border-slate-200 rounded-sm p-2 bg-slate-50" data-testid={`tpl-slot-${i}`}>
                  <div className="col-span-2">
                    <Label className="tab-tag">Day</Label>
                    <select className="w-full border border-slate-200 rounded-sm h-9 px-2 text-sm bg-white" value={s.day_of_week} onChange={(e) => patchSlot(i, { day_of_week: +e.target.value })} data-testid={`tpl-slot-${i}-day`}>
                      {DOW.map((d, idx) => <option key={d} value={idx}>{d}</option>)}
                    </select>
                  </div>
                  <div className="col-span-1"><Label className="tab-tag">Start</Label><Input className="rounded-sm mono h-9 px-2" inputMode="numeric" pattern="^([01]\d|2[0-3]):[0-5]\d$" placeholder="HHMM → HH:MM" maxLength={5} value={s.start_time} onChange={(e) => patchSlot(i, { start_time: normalizeTimeInput(e.target.value) })} data-testid={`tpl-slot-${i}-start`} /></div>
                  <div className="col-span-1"><Label className="tab-tag">End</Label><Input className="rounded-sm mono h-9 px-2" inputMode="numeric" pattern="^([01]\d|2[0-3]):[0-5]\d$" placeholder="HHMM → HH:MM" maxLength={5} value={s.end_time} onChange={(e) => patchSlot(i, { end_time: normalizeTimeInput(e.target.value) })} data-testid={`tpl-slot-${i}-end`} /></div>
                  <div className="col-span-3">
                    <Label className="tab-tag">Position</Label>
                    <select className="w-full border border-slate-200 rounded-sm h-9 px-2 text-sm bg-white" value={s.position} onChange={(e) => patchSlot(i, { position: e.target.value })} data-testid={`tpl-slot-${i}-position`}>
                      <option value="">— Choose —</option>
                      {positions.map((p) => <option key={p}>{p}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <Label className="tab-tag">Cert</Label>
                    <select className="w-full border border-slate-200 rounded-sm h-9 px-2 text-sm bg-white" value={s.required_certification} onChange={(e) => patchSlot(i, { required_certification: e.target.value })} data-testid={`tpl-slot-${i}-cert`}>
                      {certs.map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <Label className="tab-tag">Role</Label>
                    <select className="w-full border border-slate-200 rounded-sm h-9 px-2 text-sm bg-white" value={s.ambulance_role || ""} onChange={(e) => patchSlot(i, { ambulance_role: e.target.value || null })} data-testid={`tpl-slot-${i}-role`}>
                      {AMBULANCE_ROLES.map((r) => <option key={r || "none"} value={r}>{r || "—"}</option>)}
                    </select>
                  </div>
                  <div className="col-span-1"><Label className="tab-tag">×</Label><Input type="number" min="1" max="10" className="rounded-sm mono h-9 px-2" value={s.count} onChange={(e) => patchSlot(i, { count: +e.target.value })} data-testid={`tpl-slot-${i}-count`} /></div>
                  <div className="col-span-1 flex justify-end"><Button type="button" size="icon" variant="ghost" className="text-red-600" onClick={() => removeSlot(i)} data-testid={`tpl-slot-${i}-del`}><Trash size={14} /></Button></div>
                </div>
              ))}
              {template.slots.length === 0 && <div className="text-sm text-slate-500 text-center py-4">No slots — click "Add slot" to begin.</div>}
            </div>
          </div>
          <DialogFooter><Button type="submit" className="rounded-sm bg-slate-900 text-white" data-testid="tpl-save">Save Template</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ApplyTemplate({ apply, onClose, onApplied }) {
  const [weekStart, setWeekStart] = useState(isoDatePart(nextSunday(new Date())));
  const [weeks, setWeeks] = useState(1);
  const [published, setPublished] = useState(false);
  const [busy, setBusy] = useState(false);

  async function go() {
    setBusy(true);
    try {
      const { data } = await api.post(`/templates/${apply.id}/apply`, { week_start: weekStart, weeks: +weeks, published });
      toast.success(`Created ${data.created} shifts (${data.skipped_duplicates} duplicates skipped)`);
      onApplied();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  }
  return (
    <Dialog open={!!apply} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Apply: {apply?.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="tab-tag">Week starts (Sunday)</Label><Input type="date" value={weekStart} onChange={(e) => setWeekStart(e.target.value)} className="rounded-sm" data-testid="apply-date" /></div>
          <div><Label className="tab-tag">Number of weeks</Label><Input type="number" min="1" max="26" value={weeks} onChange={(e) => setWeeks(+e.target.value)} className="rounded-sm mono" data-testid="apply-weeks" /></div>
          <label className="flex items-center gap-2 text-sm border border-slate-200 rounded-sm p-2 bg-slate-50" data-testid="apply-publish-toggle">
            <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} data-testid="apply-publish" />
            <span><strong>Publish immediately</strong> · volunteers will see and be able to pick up these shifts. Leave unchecked to create as drafts.</span>
          </label>
        </div>
        <DialogFooter><Button onClick={go} disabled={busy} className="rounded-sm bg-slate-900 text-white" data-testid="apply-go">{published ? <Broadcast size={14} className="mr-1" /> : <EyeSlash size={14} className="mr-1" />}{busy ? "Applying…" : (published ? "Apply & Publish" : "Apply as Draft")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
